import type { TextFieldPropOnChange, TextFieldPropStatus, TextFieldPropValue } from '@components/FieldNumberFormat';
import { ModalDialog } from '@components/ModalDialog';
import { ProjectMethodologyEnum } from '@gql/graphql';
import { useAccess } from '@hooks/useAccess';
import { useIsIntersecting } from '@hooks/useIsIntersecting';

import { Button } from '@consta/uikit/Button';
import { cnMixSpace } from '@consta/uikit/MixSpace';
import cn from 'classnames';
import { useCallback, useRef, useState } from 'react';

import Styles from '../catalogCommonStyles.module.css';
import { CatalogEditValueInput } from '../CatalogEditValueInput';
import { TypeItem } from '../TypeItem/TypeItem';

import { useCheckIsUniqueLimitedName } from './hooks/useCheckIsUniqueName';
import { useCreateOrUpdateLimitedCostType } from './hooks/useCreateLimitedCostType';
import { useGetLimitedTypesData } from './hooks/useGetLimitedTypesData';
import { useRemoveLimitedType } from './hooks/useRemoveLimitedType';

export type TCatalogLimitedTypeProps = {
    onClose: () => void;
    methodology?: ProjectMethodologyEnum;
};

const MAX_NAME_LENGTH = 255;

export const CatalogLimitedType = ({ onClose, methodology = ProjectMethodologyEnum.Uer }: TCatalogLimitedTypeProps) => {
    const { catalogs: catalogsRules } = useAccess();

    const [isShowInput, setIsShowInput] = useState(false);
    const [newName, setNewName] = useState<TextFieldPropValue>(null);
    const [inputStatus, setInputStatus] = useState<TextFieldPropStatus | undefined>();

    const [tooltipMsg, setTooltipMsg] = useState<string | null>('');

    const { isUniqueName } = useCheckIsUniqueLimitedName({ name: newName ?? '', methodology });

    const { limitedTypesData, updateLimitedTypesData } = useGetLimitedTypesData({
        methodology,
    });

    const resetState = useCallback(() => {
        setInputStatus(undefined);
        setTooltipMsg(null);
    }, []);

    const handleRemoveInput = useCallback(() => {
        setIsShowInput(false);
        setNewName(null);
        resetState();
    }, [resetState]);

    const handleSubmit = useCreateOrUpdateLimitedCostType({
        onSuccess: useCallback(() => {
            handleRemoveInput();
            updateLimitedTypesData();
        }, [handleRemoveInput, updateLimitedTypesData]),
    });

    const handleOpenCreate = useCallback(() => {
        setIsShowInput(true);
    }, []);

    const handleChangeNewName = useCallback<TextFieldPropOnChange>(
        ({ value }) => {
            setNewName(value);
            resetState();
        },
        [resetState],
    );

    const getErrorMessage = useCallback(() => {
        const nameLength = newName?.trim().length ?? 0;

        if (!nameLength) {
            return 'Поле не может быть пустым';
        }

        if (nameLength > MAX_NAME_LENGTH) {
            return `Не более ${MAX_NAME_LENGTH} символов`;
        }

        if (!isUniqueName) {
            return 'Тип лимитированных с таким наименованием уже существует';
        }

        return null;
    }, [newName, isUniqueName]);

    const handleSave = useCallback(() => {
        const errorMsg = getErrorMessage();

        if (errorMsg) {
            setTooltipMsg(errorMsg);
            setInputStatus('alert');

            return;
        }

        if (!newName) {
            return;
        }

        handleSubmit({ methodology, name: newName });
    }, [newName, getErrorMessage, handleSubmit, methodology]);

    const listWrapperRef = useRef<HTMLDivElement>(null);
    const { refCallback, isIntersecting } = useIsIntersecting(listWrapperRef.current);

    return (
        <ModalDialog
            isOpen
            title="Типы лимитированных"
            onClose={onClose}
            bodyWidth={600}
            withBottomButtons={false}
            withTitleCloseButton
            isOnClickOutsideDisable
        >
            <div className={Styles.modalBody}>
                <div className={cnMixSpace({ mB: 'm' })}>
                    <Button
                        label="Создать новый тип лимитированных"
                        view="secondary"
                        size="s"
                        disabled={!catalogsRules.canCreateLimited}
                        onClick={handleOpenCreate}
                    />
                </div>

                <div ref={listWrapperRef} className={cn(Styles.listWrapper, cnMixSpace({ p: 'm' }))}>
                    {isShowInput && (
                        <div ref={refCallback}>
                            <CatalogEditValueInput
                                value={newName}
                                onChange={handleChangeNewName}
                                onCancel={handleRemoveInput}
                                onSubmit={handleSave}
                                status={inputStatus}
                                tooltipMsg={tooltipMsg ?? ''}
                                inputPlaceholder="Введите наименование типа лимитированных"
                                isTooltipVisible={isIntersecting}
                            />
                        </div>
                    )}

                    {limitedTypesData?.map((item) => (
                        <TypeItem
                            listWrapper={listWrapperRef.current}
                            key={item.id}
                            item={item}
                            methodology={methodology}
                            updateTypesData={updateLimitedTypesData}
                            edit={useCreateOrUpdateLimitedCostType}
                            remove={useRemoveLimitedType}
                            checkIsUniqueName={useCheckIsUniqueLimitedName}
                            editTooltipMsg="Тип лимитированных с таким наименованием уже существует"
                            editPlaceholder="Введите наименование типа лимитированных"
                            removeHeader="Удаление типа лимитированных"
                            removeQuestion="Вы уверены, что хотите удалить тип лимитированных?"
                            cantDeleteTooltip="Тип лимитированных включен в формулу лимитированных затрат"
                        />
                    ))}
                </div>
            </div>
        </ModalDialog>
    );
};

CatalogLimitedType.displayName = 'CatalogLimitedType';
